import { deliveryReport } from "./deliveryReport";

/** Что известно об этом устройстве и его подписке. Адрес подписки — секрет, в отчёт не попадает. */
export interface BrowserPushState {
  platform: string | null;
  installedApp: boolean;
  permission: NotificationPermission | "unsupported";
  /** Service worker запущен и может принять push. */
  workerActive: boolean;
  subscription: null | {
    /** Отпечаток адреса: по нему подписка сопоставляется с записью на сервере. */
    fingerprint: string;
    pushService: string | null;
    /** Ключ VAPID, с которым подписка создана (открытая часть). */
    applicationServerKey: string | null;
  };
}

export interface ServerDevice {
  id: string;
  name: string;
  fingerprint: string;
  lastDeliveredAt: string | null;
  /** Отказ push-службы после последней успешной отправки: код и причина. */
  lastError?: { at: string; summary: string } | null;
}

export interface PushDiagnosticsDeps {
  browser: () => Promise<BrowserPushState>;
  /** Открытый ключ VAPID, который сервер использует сейчас. */
  vapidPublicKey: () => Promise<string | null>;
  /** Значение канала «на устройство» в общем правиле. */
  generalWebPush: () => Promise<boolean | null>;
  subscriptions: () => Promise<ServerDevice[]>;
  queueHealth: () => Promise<unknown>;
  outboxHealth: () => Promise<unknown>;
  sendProbe?: () => Promise<unknown>;
  sleep: (milliseconds: number) => Promise<void>;
  now: () => Date;
}

export interface PushDiagnosticsOptions {
  /** Отправить проверочное уведомление и понаблюдать, что произойдёт. */
  probe?: boolean;
  /** Сколько секунд наблюдать за доставкой. */
  waitSeconds?: number;
}

type Settled<T> = { ok: true; value: T } | { ok: false; error: string };

async function settle<T>(action: () => Promise<T>): Promise<Settled<T>> {
  try {
    return { ok: true, value: await action() };
  } catch (error) {
    return { ok: false, error: error instanceof Error ? `${error.name}: ${error.message}` : String(error) };
  }
}

const orError = <T>(result: Settled<T>): T | { error: string } => (result.ok ? result.value : { error: result.error });

function record(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null ? (value as Record<string, unknown>) : {};
}

/** Ключ VAPID приходит то с «=» на конце, то без: сравниваем без выравнивания. */
const normalizeKey = (key: string | null) => (key === null ? null : key.replace(/=+$/, ""));

/** Очередь и задачи, от которых зависит доставка уведомлений; всё прочее (почта, выгрузки) — не про диагностику. */
const deliveryQueues = new Set(["notifications", "events"]);
const deliveryJob = /notification|outbox|push|deliver/i;

/**
 * Что из ответа сервера об очередях относится к доставке уведомлений.
 *
 * В ответе — вся история упавших задач всех очередей: сорвавшаяся синхронизация
 * почты недельной давности не объясняет, почему не пришёл push, и только
 * заслоняет причину. Берём очередь уведомлений и упавшие задачи доставки.
 */
export function summarizeQueue(health: unknown) {
  const data = record(health);
  const queues = Array.isArray(data.queues) ? data.queues.map(record) : [];
  const notifications = queues.find((queue) => queue.queue === "notifications");
  const failures = (Array.isArray(data.recent_failures) ? data.recent_failures.map(record) : []).filter(
    (failure) => deliveryQueues.has(String(failure.queue)) || deliveryJob.test(String(failure.job)),
  );

  return {
    waiting: typeof notifications?.depth === "number" ? notifications.depth : null,
    oldestAgeSeconds: typeof notifications?.oldest_age_seconds === "number" ? notifications.oldest_age_seconds : null,
    failures: failures.map((failure) => ({
      job: String(failure.job).split("\\").pop(),
      at: String(failure.failed_at),
      error: String(failure.error),
    })),
  };
}

/** События в outbox, которые ещё не стали уведомлениями. */
export function summarizeOutbox(health: unknown) {
  const data = record(health);

  return {
    waiting: typeof data.pending === "number" ? data.pending : null,
    failed: typeof data.failed === "number" ? data.failed : null,
    oldestWaitingAt: typeof data.oldest_pending_at === "string" ? data.oldest_pending_at : null,
  };
}

/** Проблемы простыми словами. Только то, что мешает доставке сейчас, а не история. */
function problems(
  browser: Settled<BrowserPushState>,
  serverKey: Settled<string | null>,
  general: Settled<boolean | null>,
  devices: Settled<ServerDevice[]>,
  queue: ReturnType<typeof summarizeQueue> | null,
  outbox: ReturnType<typeof summarizeOutbox> | null,
  now: Date,
): string[] {
  const result: string[] = [];

  if (browser.ok) {
    const state = browser.value;
    if (state.permission === "unsupported") {
      result.push("Устройство не поддерживает push: нужен https, service worker и (на iPhone) установленное приложение.");
    } else if (state.permission !== "granted") {
      result.push(`Разрешение на уведомления не выдано (${state.permission}).`);
    }
    if (state.permission !== "unsupported" && !state.workerActive) {
      result.push("Service worker не активен: push принимать некому.");
    }
    if (state.subscription === null && state.permission === "granted") {
      result.push("Разрешение есть, но подписки в браузере нет: устройство не подключено.");
    }
    if (state.subscription !== null && devices.ok && !devices.value.some((d) => d.fingerprint === state.subscription?.fingerprint)) {
      result.push("Подписка есть в браузере, но сервер о ней не знает: нажмите «Подключить это устройство».");
    }
    if (state.subscription !== null && serverKey.ok) {
      const mine = normalizeKey(state.subscription.applicationServerKey);
      const theirs = normalizeKey(serverKey.value);
      if (mine !== null && theirs !== null && mine !== theirs) {
        result.push("Ключ VAPID на сервере отличается от ключа подписки этого устройства: push-служба отклонит отправку. Отключите устройство и подключите заново.");
      }
    }
  }
  if (serverKey.ok && serverKey.value === null) result.push("На сервере не настроен ключ VAPID: push отправлять нечем.");
  if (general.ok && general.value === false) result.push("В общем правиле выключена доставка «На устройство»: сервер не отправляет push.");
  if (devices.ok && devices.value.length === 0) result.push("На сервере нет ни одной подписки: отправлять некуда.");
  if (devices.ok) {
    for (const device of devices.value) {
      if (device.lastError != null) result.push(`Push-служба отклонила отправку на «${device.name}»: ${device.lastError.summary}`);
    }
  }
  if (queue !== null) {
    if (queue.oldestAgeSeconds !== null && queue.oldestAgeSeconds > 60) {
      result.push(`Очередь уведомлений не разбирается: старейшая задача ждёт ${queue.oldestAgeSeconds} с. Вероятно, не работает воркер (queue-fast).`);
    }
    for (const failure of queue.failures) result.push(`Упала задача доставки ${failure.job} (${failure.at}): ${failure.error}`);
  }
  if (outbox !== null) {
    if (outbox.failed !== null && outbox.failed > 0) result.push(`В outbox есть события с ошибкой отправки: ${outbox.failed}.`);
    // Пара секунд ожидания — норма; «застряло» — когда событие ждёт минуты.
    if (outbox.waiting !== null && outbox.waiting > 0 && outbox.oldestWaitingAt !== null) {
      const age = (now.getTime() - new Date(outbox.oldestWaitingAt).getTime()) / 1000;
      if (age > 120) result.push(`События застряли в outbox (${outbox.waiting}, старейшее ${Math.round(age)} с): проверьте планировщик (scheduler).`);
    }
  }

  return result;
}

/**
 * Собирает короткий отчёт о доставке push и, по желанию, проверяет её вживую.
 *
 * Нужен там, где журналов сервера не видно: человек запускает его в консоли
 * или кнопкой на странице и присылает текст. В отчёте только то, что нужно для
 * диагноза: состояние этого устройства, подключённые устройства с итогом
 * последней отправки, три показателя сервера и результат проверки. Нет истории
 * событий, посторонних очередей, отпечатков, идентификаторов, ключей и адресов
 * подписок. Каждая часть собирается отдельно: сбой одной не отменяет остальные.
 */
export async function collectPushDiagnostics(deps: PushDiagnosticsDeps, options: PushDiagnosticsOptions = {}) {
  const [browser, serverKey, general, devices, queueRaw, outboxRaw] = await Promise.all([
    settle(deps.browser),
    settle(deps.vapidPublicKey),
    settle(deps.generalWebPush),
    settle(deps.subscriptions),
    settle(deps.queueHealth),
    settle(deps.outboxHealth),
  ]);
  const queue = queueRaw.ok ? summarizeQueue(queueRaw.value) : null;
  const outbox = outboxRaw.ok ? summarizeOutbox(outboxRaw.value) : null;

  const probe =
    options.probe === true && deps.sendProbe !== undefined
      ? await observeProbe(deps, devices.ok ? devices.value : [], options.waitSeconds ?? 30)
      : undefined;

  const found = problems(browser, serverKey, general, devices, queue, outbox, deps.now());
  if (probe !== undefined && probe.sent && probe.notDelivered.length > 0) {
    found.push(`Доставка не подтверждена для: ${probe.notDelivered.join(", ")}.`);
  }
  if (probe !== undefined && !probe.sent) found.push(`Проверочное уведомление не отправилось: ${probe.error}`);

  const here = browser.ok ? browser.value.subscription?.fingerprint : undefined;
  const state = browser.ok ? browser.value : null;

  return {
    generatedAt: deps.now().toISOString(),
    status: found.length === 0 ? "ok" : "problems",
    summary: summary(found, probe),
    problems: found,
    thisDevice: state
      ? {
          platform: state.platform,
          installedApp: state.installedApp,
          permission: state.permission,
          workerActive: state.workerActive,
          pushService: state.subscription?.pushService ?? null,
          subscribed: state.subscription !== null,
          knownToServer: devices.ok && here !== undefined ? devices.value.some((d) => d.fingerprint === here) : null,
          vapidMatches:
            state.subscription !== null && serverKey.ok
              ? normalizeKey(state.subscription.applicationServerKey) === normalizeKey(serverKey.value)
              : null,
        }
      : orError(browser),
    server: {
      deliveryToDevicesEnabled: orError(general),
      vapidConfigured: serverKey.ok ? serverKey.value !== null : orError(serverKey),
      notificationsQueue: queue !== null ? { waiting: queue.waiting, oldestAgeSeconds: queue.oldestAgeSeconds } : { error: queueRaw.ok ? null : queueRaw.error },
      outbox: outbox !== null ? { waiting: outbox.waiting, failed: outbox.failed } : { error: outboxRaw.ok ? null : outboxRaw.error },
    },
    devices: devices.ok
      ? devices.value.map((device) => ({
          name: device.name,
          thisDevice: here !== undefined && device.fingerprint === here,
          lastDeliveredAt: device.lastDeliveredAt,
          ...(device.lastError == null ? {} : { lastError: device.lastError.summary }),
        }))
      : orError(devices),
    ...(probe === undefined ? {} : { probe: probe.sent ? { delivered: probe.delivered, notDelivered: probe.notDelivered, removed: probe.removed, waitedSeconds: probe.waitedSeconds } : { sent: false, error: probe.error } }),
  };
}

function summary(found: string[], probe: Awaited<ReturnType<typeof observeProbe>> | undefined): string {
  const checked =
    probe !== undefined && probe.sent
      ? ` Проверка: принято push-службой для ${probe.delivered.length} из ${probe.delivered.length + probe.notDelivered.length + probe.removed.length} устройств.`
      : "";

  return found.length === 0 ? `Замечаний нет.${checked}` : `Найдено проблем: ${found.length}.${checked}`;
}

type ProbeResult =
  | { sent: false; error: string }
  | { sent: true; delivered: string[]; notDelivered: string[]; removed: string[]; waitedSeconds: number };

/**
 * Отправляет проверочное уведомление и ждёт, пока сервер отметит доставку.
 *
 * Останавливается, когда ответили все устройства, либо когда после первых
 * подтверждений три опроса подряд ничего не меняется: остальные устройства, как
 * правило, — старые записи, которые не ответят никогда, и ждать их незачем.
 */
async function observeProbe(deps: PushDiagnosticsDeps, before: ServerDevice[], waitSeconds: number): Promise<ProbeResult> {
  const beforeMap = new Map(before.map((device) => [device.id, device.lastDeliveredAt]));
  const sent = await settle(() => deps.sendProbe!());
  if (!sent.ok) return { sent: false, error: sent.error };

  const step = 3;
  let latest: ServerDevice[] = before;
  let waited = 0;
  let lastChange = 0;
  let previousConfirmed = 0;
  const confirmedNow = () => latest.filter((d) => d.lastDeliveredAt !== null && d.lastDeliveredAt !== (beforeMap.get(d.id) ?? null)).length;
  while (waited < waitSeconds) {
    await deps.sleep(step * 1000);
    waited += step;
    const devices = await settle(deps.subscriptions);
    if (devices.ok) latest = devices.value;
    const now = new Map(latest.map((device) => [device.id, device.lastDeliveredAt]));
    const confirmed = confirmedNow();
    if (confirmed !== previousConfirmed) {
      previousConfirmed = confirmed;
      lastChange = waited;
    }
    const answered = before.every((d) => !now.has(d.id) || (now.get(d.id) ?? null) !== (beforeMap.get(d.id) ?? null));
    if (before.length > 0 && answered) break;
    if (confirmed > 0 && waited - lastChange >= 9) break;
  }

  const now = new Map(latest.map((device) => [device.id, device.lastDeliveredAt]));
  const report = deliveryReport(
    before.map((device) => ({
      name: device.name,
      before: device.lastDeliveredAt,
      after: now.has(device.id) ? (now.get(device.id) ?? null) : undefined,
    })),
  );

  return { sent: true, delivered: report.confirmed, notDelivered: report.missing, removed: report.revoked, waitedSeconds: waited };
}
